import { Injectable } from '@angular/core';

export interface GoogleTagManagerConfig {
    enabled: boolean;
    containerId: string;
}

export interface GoogleAnalyticsConfig {
    enabled: boolean;
    trackingId: string;
}

export interface FacebookPixelConfig {
    enabled: boolean;
    pixelId: string;
}

export interface MicrosoftClarityConfig {
    enabled: boolean;
    projectId: string;
}

export interface ThirdPartyScriptsConfig {
    googleTagManager?: GoogleTagManagerConfig;
    googleAnalytics?: GoogleAnalyticsConfig;
    facebookPixel?: FacebookPixelConfig;
    microsoftClarity?: MicrosoftClarityConfig;
}

export type ThirdPartyScriptTool = 'googleTagManager' | 'googleAnalytics' | 'facebookPixel' | 'microsoftClarity';

export const THIRD_PARTY_SCRIPT_TOOLS: ThirdPartyScriptTool[] = ['googleTagManager', 'googleAnalytics', 'facebookPixel', 'microsoftClarity'];

@Injectable({ providedIn: 'root' })
export class ThirdPartyScriptsService {
    private readonly injectedScripts = new Set<string>();

    init(config: ThirdPartyScriptsConfig): void {
        if (config.googleTagManager?.enabled) {
            this.injectGoogleTagManager(config.googleTagManager.containerId);
        }

        if (config.googleAnalytics?.enabled) {
            this.injectGoogleAnalytics(config.googleAnalytics.trackingId);
        }

        if (config.facebookPixel?.enabled) {
            this.injectFacebookPixel(config.facebookPixel.pixelId);
        }

        if (config.microsoftClarity?.enabled) {
            this.injectMicrosoftClarity(config.microsoftClarity.projectId);
        }
    }

    getEnabledTools(config: ThirdPartyScriptsConfig): ThirdPartyScriptTool[] {
        return THIRD_PARTY_SCRIPT_TOOLS.filter((tool) => config[tool]?.enabled);
    }

    private injectGoogleTagManager(containerId: string): void {
        const key = `gtm-${containerId}`;
        if (this.injectedScripts.has(key)) {
            return;
        }
        this.injectedScripts.add(key);

        const script = document.createElement('script');
        script.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${containerId}');`;
        document.head.appendChild(script);

        const noscript = document.createElement('noscript');
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        noscript.appendChild(iframe);
        document.body.insertBefore(noscript, document.body.firstChild);
    }

    private injectGoogleAnalytics(trackingId: string): void {
        const key = `ga-${trackingId}`;
        if (this.injectedScripts.has(key)) {
            return;
        }
        this.injectedScripts.add(key);

        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
        document.head.appendChild(script);

        const configScript = document.createElement('script');
        configScript.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${trackingId}');`;
        document.head.appendChild(configScript);
    }

    private injectFacebookPixel(pixelId: string): void {
        const key = `fb-${pixelId}`;
        if (this.injectedScripts.has(key)) {
            return;
        }
        this.injectedScripts.add(key);

        const script = document.createElement('script');
        script.textContent = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${pixelId}');fbq('track','PageView');`;
        document.head.appendChild(script);

        const noscript = document.createElement('noscript');
        const img = document.createElement('img');
        img.height = 1;
        img.width = 1;
        img.style.display = 'none';
        img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
        noscript.appendChild(img);
        document.body.insertBefore(noscript, document.body.firstChild);
    }

    private injectMicrosoftClarity(projectId: string): void {
        const key = `clarity-${projectId}`;
        if (this.injectedScripts.has(key)) {
            return;
        }
        this.injectedScripts.add(key);

        const script = document.createElement('script');
        script.textContent = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "${projectId}");`;
        document.head.appendChild(script);
    }
}
